import * as React from "react";
import { Email, LTLShipment } from "@src/types/api";
import { emailShipmentsLtl } from "@taskpane/utils/emailShipments";
import type { LoadApplication } from "@src/load-agent/LoadApplication";
import { statusHistoryIdentityPayload } from "@src/load-agent/office/resolveActorEmailForThread";
import type { LTLState } from "./ltlReducer";

const SELECTED_MODE_LTL = "LTL";

export interface PriceOptions {
  state: LTLState;
  loaderRef: React.RefObject<HTMLElement | null>;
}

export interface PriceResult {
  success: boolean;
  refreshedEmail?: Email | null;
  shipmentId?: string;
}

export interface LtlPriceContext {
  loadApp: Pick<
    LoadApplication,
    "createLTLShipment" | "saveLTLPricing" | "getLTLPricing" | "getEmails"
  >;
  emailData: Email | null;
  clickedLTLShipment: LTLShipment | null;
  setEmailData: (emailData: Email | null) => void;
  setClickedLTLShipment: (shipment: LTLShipment | null) => void;
  setError: (msg: string | null) => void;
}

export async function runLtlPriceRequest(
  ctx: LtlPriceContext,
  { state, loaderRef }: PriceOptions
): Promise<PriceResult> {
  const { loadApp, emailData, clickedLTLShipment, setEmailData, setClickedLTLShipment, setError } =
    ctx;
  const internetMessageId = emailData?.internetMessageId;

  if (!internetMessageId) {
    return { success: false };
  }
  let shipmentId = clickedLTLShipment?.shipmentId;
  let shipmentForSave = clickedLTLShipment;

  if (!shipmentId) {
    const created = await loadApp.createLTLShipment({
      internetMessageId,
      selectedMode: SELECTED_MODE_LTL,
      formData: state.form,
      shipmentMode: state.shipmentMode,
      items: state.items,
      accessorials: state.accessorials,
      equipment: state.equipment,
    });

    shipmentId = created.shipmentId;
    shipmentForSave = (created.shipment as LTLShipment | undefined) ?? null;
  }
  await loadApp.saveLTLPricing({
    shipmentId: shipmentId as string,
    internetMessageId,
    selectedMode: SELECTED_MODE_LTL,
    formData: state.form,
    existingShipment: shipmentForSave,
    accessorials: state.accessorials,
    items: state.items,
    shipmentMode: state.shipmentMode,
    equipment: state.equipment,
  });
  if (loaderRef.current) {
    const offset = 100;
    const top = loaderRef.current.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({ top: top + offset, behavior: "smooth" });
  }
  const pricingResult = (await loadApp.getLTLPricing(
    shipmentId as string,
    internetMessageId,
    statusHistoryIdentityPayload(emailData)
  )) ?? {
    hasOffers: true,
  };
  const refreshedEmail = await loadApp.getEmails(internetMessageId);

  setEmailData(refreshedEmail);
  const updatedShipment = refreshedEmail
    ? emailShipmentsLtl(refreshedEmail).find((s: LTLShipment) => s.shipmentId === shipmentId)
    : undefined;

  if (updatedShipment) {
    setClickedLTLShipment(updatedShipment);
  }
  if (!pricingResult.hasOffers) {
    setError(pricingResult.message || "No offers returned from the API.");
  }
  return { success: true, refreshedEmail, shipmentId };
}

export interface SelectOfferOptions {
  shipmentId: string;
  selectedOfferIds: string[];
  margins: Record<string, number>;
  ltlMarginModes?: Record<string, "percent" | "dollar">;
  ltlMarginDollars?: Record<string, number>;
}

export async function runLtlSelectOffer(
  loadApp: Pick<LoadApplication, "saveLTLSelectedOffer" | "getEmails">,
  emailData: Email | null,
  setEmailData: (emailData: Email | null) => void,
  { shipmentId, selectedOfferIds, margins, ltlMarginModes, ltlMarginDollars }: SelectOfferOptions
): Promise<boolean> {
  const internetMessageId = emailData?.internetMessageId;

  if (!internetMessageId) {
    throw new Error("internetMessageId not available");
  }
  await loadApp.saveLTLSelectedOffer({
    offerIds: selectedOfferIds,
    margins,
    shipmentId,
    internetMessageId,
    ...(ltlMarginModes ? { ltlMarginModes } : {}),
    ...(ltlMarginDollars ? { ltlMarginDollars } : {}),
    ...statusHistoryIdentityPayload(emailData),
  });
  const refreshedEmail = await loadApp.getEmails(internetMessageId);

  setEmailData(refreshedEmail);
  return true;
}
